import { useEffect, useState } from 'react';
import { useNavigate } from '../routing';
import EmptyState from './EmptyState';
import { analysisApi } from '../utils/api';
import { useAuth } from '../context/AuthContext';

// The "nothing to show" copy for the two by-supplier spend views — the donut
// card on /analysis and the full report on /analysis/reports/suppliers. One
// component so both speak with the same voice, the same way PaymentEmptyState
// serves the two payment tabs.
//
// Three genuinely different facts, which must never borrow each other's words:
//
//   no-match      a name/product filter is on and matched nothing. The period
//                 HAS data. Transient "adjust the search" moment → compact.
//   empty-period  the account has purchases, just not in this period. Also
//                 transient — step the period → compact.
//   new-account   the restaurant has no purchase data at all. This is a
//                 welcome, not a failure: the mermaid comes out and the copy
//                 looks forward.
//
// Saying "אין נתוני רכש" when a filter simply matched nothing reads as "my data
// disappeared", which is the same class of lie InlineLoadError exists to avoid.
//
// Copy is gender-neutral (impersonal/infinitive) per the house style.

export default function SupplierSpendEmptyState({ period, filterActive = false }) {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  // Has this restaurant EVER had a document? The spend endpoints are
  // date-scoped, so an empty period cannot answer it. `/analysis/has-history`
  // is a single EXISTS on an index — it does not re-run the spend aggregate —
  // and it is asked from inside this component, which only mounts once a screen
  // is already empty. So the request never fires on the happy path, and both
  // callers get the behaviour without wiring it themselves.
  //   null = still asking · true = has history · false = brand new
  const [everHadPurchases, setEverHadPurchases] = useState(null);

  useEffect(() => {
    // A filter miss says nothing about the account, so don't spend a request.
    if (filterActive) return;
    let cancelled = false;
    analysisApi.hasHistory()
      .then(r => { if (!cancelled) setEverHadPurchases(Boolean(r.data?.has_history)); })
      // On failure assume there IS history: greeting an established customer
      // as brand new is the worse of the two mistakes.
      .catch(() => { if (!cancelled) setEverHadPurchases(true); });
    return () => { cancelled = true; };
  }, [filterActive]);

  if (filterActive) {
    return (
      <EmptyState compact message={`לא נמצאו ספקים ב${period} לפי הסינון שנבחר`}>
        <p className="empty-subline">אפשר לנקות את החיפוש או לבחור תקופה אחרת.</p>
      </EmptyState>
    );
  }

  // Until the probe answers, use the period wording. It is the common case, and
  // it never over-claims — worst case a brand-new account sees it for a moment
  // before the warmer welcome replaces it.
  if (everHadPurchases !== false) {
    return (
      <EmptyState compact message={`אין נתוני רכש ב${period}`}>
        <p className="empty-subline">אפשר לדלג לתקופה אחרת כדי לראות רכש קודם.</p>
      </EmptyState>
    );
  }

  // Brand new: the mermaid moment.
  return (
    <EmptyState message="כאן יופיע ניתוח הרכש">
      <p className="empty-subline">
        ברגע שייקלטו המסמכים הראשונים, אפשר יהיה לראות כאן איך ההוצאה מתחלקת
        בין הספקים — מי הגדולים, ומה הנתח של כל אחד.
      </p>
      {isAdmin && (
        <button type="button" className="empty-action" onClick={() => navigate('/upload')}>
          להעלאת המסמך הראשון
        </button>
      )}
    </EmptyState>
  );
}
